import { useEffect } from "react";
import type { Page, Story } from "../models/story";

type Params = {
  /** When false, no listener is registered. */
  enabled: boolean;
  story: Story | undefined;
  firstVisiblePage: Page | undefined;
  lastVisiblePage: Page | undefined;
  onPrev: () => void;
  onNext: () => void;
};

/**
 * ArrowLeft / ArrowRight match the story reader’s Previous / Next actions.
 * Ignored while typing in form fields.
 */
export function useStoryArrowKeys({
  enabled,
  story,
  firstVisiblePage,
  lastVisiblePage,
  onPrev,
  onNext,
}: Params): void {
  useEffect(() => {
    if (!enabled || !story || !firstVisiblePage || !lastVisiblePage) return;

    const s = story;
    const first = firstVisiblePage;
    const last = lastVisiblePage;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const t = e.target;
      if (
        t instanceof Element &&
        t.closest("input, textarea, select, [contenteditable='true']")
      ) {
        return;
      }

      if (e.key === "ArrowLeft") {
        if (!s.hasPreviousPage(first)) return;
        e.preventDefault();
        onPrev();
        return;
      }
      if (e.key === "ArrowRight") {
        if (!s.hasNextPage(last)) return;
        e.preventDefault();
        onNext();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, story, firstVisiblePage, lastVisiblePage, onPrev, onNext]);
}
